import React, { useEffect, useState } from 'react';
import { 
  Calendar, Download, Filter, MoreVertical, ArrowDownLeft, TrendingUp, TrendingDown 
} from 'lucide-react';
import { cn } from '../lib/utils';
import { formatCurrency, getDefaultCurrency } from '../lib/currency';

interface BalanceSheetData {
  assets: Record<string, number>;
  liabilities: Record<string, number>;
  equity: Record<string, number>;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  comparison?: Omit<BalanceSheetData, 'comparison'>;
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeBalanceSheetSection = (section: unknown): Record<string, number> => {
  const entries: Array<[string, unknown]> = Array.isArray(section)
    ? section.map((row: any, index: number) => [
        row?.name ?? row?.accountName ?? row?.account_name ?? row?.code ?? String(index),
        row,
      ])
    : Object.entries((section && typeof section === 'object' ? section : {}) as Record<string, unknown>);

  return entries.reduce<Record<string, number>>((normalized, [key, row]) => {
    const values = row && typeof row === 'object' ? row as Record<string, unknown> : { balance: row };
    const name = String(values.name ?? values.accountName ?? values.account_name ?? key);
    normalized[name] = toNumber(values.balance ?? values.amount ?? values.value) ?? 0;
    return normalized;
  }, {});
};

const normalizeBalanceSheet = (payload: any): BalanceSheetData => {
  const report = payload?.data ?? payload;
  const assets = normalizeBalanceSheetSection(report?.assets);
  const liabilities = normalizeBalanceSheetSection(report?.liabilities);
  const equity = normalizeBalanceSheetSection(report?.equity);
  const total = (value: unknown, section: Record<string, number>) =>
    toNumber(value) ?? Object.values(section).reduce((sum, amount) => sum + amount, 0);

  const result = {
    assets,
    liabilities,
    equity,
    totalAssets: total(report?.totalAssets ?? report?.total_assets, assets),
    totalLiabilities: total(report?.totalLiabilities ?? report?.total_liabilities, liabilities),
    totalEquity: total(report?.totalEquity ?? report?.total_equity, equity),
  };
  return { ...result, comparison: report?.comparison ? normalizeBalanceSheet(report.comparison) : undefined };
};

interface BalanceSheetReportProps {
  endDate: string;
  setEndDate: (date: string) => void;
  compareWith: string;
  setCompareWith: (val: string) => void;
  basis: 'Accrual' | 'Cash';
  setBasis: (basis: 'Accrual' | 'Cash') => void;
  showZeroBalance: boolean;
  setShowZeroBalance: (val: boolean) => void;
  showAccountingBasis: boolean;
  setShowAccountingBasis: (val: boolean) => void;
  showAccountCodes: boolean;
  setShowAccountCodes: (val: boolean) => void;
  showDecimals: boolean;
  setShowDecimals: (val: boolean) => void;
  isFilterMenuOpen: boolean;
  setIsFilterMenuOpen: (val: boolean) => void;
  isMoreMenuOpen: boolean;
  setIsMoreMenuOpen: (val: boolean) => void;
  handleExport: () => void;
  calculateBalanceSheet: () => {
    assets: Record<string, number>;
    liabilities: Record<string, number>;
    equity: Record<string, number>;
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
  };
  setActiveReport: (report: string | null) => void;
  currency?: string;
}

export const BalanceSheetReport = ({
  endDate, setEndDate, compareWith, setCompareWith,
  basis, setBasis, showZeroBalance, setShowZeroBalance,
  showAccountingBasis, setShowAccountingBasis, showAccountCodes, setShowAccountCodes,
  showDecimals, setShowDecimals, isFilterMenuOpen, setIsFilterMenuOpen,
  isMoreMenuOpen, setIsMoreMenuOpen, handleExport,
  calculateBalanceSheet, setActiveReport, currency = 'USD'
}: BalanceSheetReportProps) => {
  const [localBalanceSheet, setLocalBalanceSheet] = useState<BalanceSheetData | null>(null);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    const orgId = typeof window !== 'undefined' ? localStorage.getItem('selected_org_id') : null;

    if (!token || !orgId) {
      setLocalBalanceSheet(null);
      return;
    }

    const controller = new AbortController();
    const queryParams = new URLSearchParams();
    if (endDate) queryParams.set('asOf', endDate);
    if (compareWith !== 'None') {
      const years = Number.parseInt(compareWith, 10);
      if (Number.isFinite(years)) {
        const comparisonDate = new Date(`${endDate}T00:00:00`);
        comparisonDate.setFullYear(comparisonDate.getFullYear() - years);
        queryParams.set('compareAsOf', comparisonDate.toISOString().slice(0, 10));
      }
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''; 

    setLocalBalanceSheet(null);
    fetch(`/api/organizations/${encodeURIComponent(orgId)}/reports/balance-sheet${query}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load balance sheet report');
        return response.json();
      })
      .then(payload => setLocalBalanceSheet(normalizeBalanceSheet(payload)))
      .catch((error: any) => {
        if (error.name !== 'AbortError') setLocalBalanceSheet(null);
      });

    return () => controller.abort();
  }, [endDate, compareWith]);

  const { assets, liabilities, equity, totalAssets, totalLiabilities, totalEquity } =
    localBalanceSheet ?? calculateBalanceSheet();
  const comparison = localBalanceSheet?.comparison;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date:</span>
            <select 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value={new Date().toISOString().split('T')[0]}>Today</option>
              <option value={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]}>End of this month</option>
              <option value={new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString().split('T')[0]}>End of last month</option>
              <option value={new Date(new Date().getFullYear(), (Math.floor(new Date().getMonth() / 3) + 1) * 3, 0).toISOString().split('T')[0]}>End of last quarter</option>
              <option value={new Date(new Date().getFullYear(), 0, 0).toISOString().split('T')[0]}>End of last financial year</option>
            </select>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Compare with</span>
            <select 
              value={compareWith}
              onChange={(e) => setCompareWith(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="None">None</option>
              <option value="1 year">1 year</option>
              <option value="2 years">2 years</option>
              <option value="3 years">3 years</option>
              <option value="4 years">4 years</option>
            </select>
          </div>

          <button 
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
          >
            <Filter className="w-4 h-4" />
            Filter
          </button>

          <div className="relative">
            <button 
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
            >
              <MoreVertical className="w-4 h-4" />
              More
            </button>
            
            {isMoreMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl shadow-xl z-50 p-4 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Accounting basis</p>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setBasis('Accrual')}
                      className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Accrual' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                    >
                      Accrual
                    </button>
                    <button 
                      onClick={() => setBasis('Cash')}
                      className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Cash' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                    >
                      Cash
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Include accounts with</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showZeroBalance} onChange={(e) => setShowZeroBalance(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Zero balance or activity</span>
                  </label>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Show</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showAccountingBasis} onChange={(e) => setShowAccountingBasis(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Accounting basis</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showAccountCodes} onChange={(e) => setShowAccountCodes(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Account codes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showDecimals} onChange={(e) => setShowDecimals(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Decimals</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={handleExport}
            className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Generate Report
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Balance Sheet</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            Demo Company (Global)
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            As at {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          {comparison && <p className="text-xs font-bold opacity-50 mt-2">Compared with {compareWith}: assets {formatCurrency(comparison.totalAssets, currency)}</p>}
        </div>

        <div className="space-y-12 max-w-3xl mx-auto">
          <section className="space-y-4">
            <h3 className="text-xl font-bold italic serif border-b-2 border-[#141414] pb-2">Assets</h3>
            {Object.entries(assets).filter(([, amount]) => showZeroBalance || amount !== 0).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">{formatCurrency(amount, currency)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Assets</span>
              <span className="text-lg font-mono font-bold">{formatCurrency(totalAssets, currency)}</span>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xl font-bold italic serif border-b-2 border-[#141414] pb-2">Liabilities</h3>
            {Object.entries(liabilities).filter(([, amount]) => showZeroBalance || amount !== 0).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">{formatCurrency(amount, currency)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Liabilities</span>
              <span className="text-lg font-mono font-bold">{formatCurrency(totalLiabilities, currency)}</span>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xl font-bold italic serif border-b-2 border-[#141414] pb-2">Equity</h3>
            {Object.entries(equity).filter(([, amount]) => showZeroBalance || amount !== 0).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">{formatCurrency(amount, currency)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Equity</span>
              <span className="text-lg font-mono font-bold">{formatCurrency(totalEquity, currency)}</span>
            </div>
          </section>

          <section className="pt-8 border-t-4 border-double border-[#141414]">
            <div className="flex justify-between items-center p-8 bg-[#141414] text-[#E4E3E0] rounded-[32px] shadow-xl">
              <div>
                <h3 className="text-sm uppercase tracking-widest font-bold opacity-50">Total Liabilities & Equity</h3>
                <p className="text-4xl font-bold tracking-tighter italic serif mt-2">
                  {formatCurrency(totalLiabilities + totalEquity, currency)}
                </p>
              </div>
              {Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01 ? (
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-green-400 mb-1">Balanced</span>
                  <TrendingUp className="w-12 h-12 text-green-400" />
                </div>
              ) : (
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1">Unbalanced</span>
                  <TrendingDown className="w-12 h-12 text-red-400" />
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
